/**
 * Cookie Dialog Component
 * Full cookie settings modal with all categories and controls
 * Properly scrollable on mobile/tablet with header-matching button colors
 */

import { ArrowLeft, X, ExternalLink } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import type { CookiePreferences } from '../types';
import { DIALOG_CONTENT, COOKIE_CATEGORIES } from '../constants';
import { CookieCategory } from './CookieCategory';

interface CookieDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Current preferences */
  preferences: CookiePreferences;
  /** Update a preference */
  onUpdatePreference: (key: keyof Omit<CookiePreferences, 'necessary'>, value: boolean) => void;
  /** Accept all cookies */
  onAcceptAll: () => void;
  /** Accept selected cookies */
  onAcceptSelected: () => void;
}

export function CookieDialog({
  open,
  onClose,
  preferences,
  onUpdatePreference,
  onAcceptAll,
  onAcceptSelected,
}: CookieDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogPrimitive.Portal>
        {/* Overlay - very high z-index to be above header */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[9999] bg-[#00000040] backdrop-blur-[3px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />

        {/* Content - very high z-index */}
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[10000] bg-white shadow-2xl',
            // Positioning
            'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            // Size - responsive with better mobile handling
            'w-[calc(100%-2rem)] max-w-2xl',
            // Height constraints for proper scrolling
            'max-h-[85vh] tablet:max-h-[80vh]',
            // Shape - nice rounded corners
            'rounded-2xl tablet:rounded-3xl',
            // Flex container for proper layout
            'flex flex-col',
            // Animation
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-300'
          )}
        >
          {/* Header - fixed at top */}
          <div className="shrink-0 bg-white border-b border-gray-100 px-5 py-4 tablet:px-6 tablet:py-5 rounded-t-2xl tablet:rounded-t-3xl relative">
            <DialogPrimitive.Title className="text-base tablet:text-xl font-bold text-gray-900 pr-10">
              {DIALOG_CONTENT.title}
            </DialogPrimitive.Title>

            {/* Close button */}
            <DialogPrimitive.Close
              className={cn(
                'absolute right-4 top-4',
                'inline-flex items-center justify-center rounded-full p-2',
                'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#014383]'
              )}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Κλείσιμο</span>
            </DialogPrimitive.Close>
          </div>

          {/* Scrollable content area */}
          <div
            className={cn(
              'flex-1 overflow-y-auto overscroll-contain',
              // Custom scrollbar styling
              'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent',
              // Smooth scrolling
              'scroll-smooth'
            )}
          >
            {/* Description */}
            <div className="px-5 py-4 tablet:px-6 tablet:py-5 border-b border-gray-100 bg-gradient-to-b from-gray-50/50 to-white">
              <DialogPrimitive.Description className="text-xs tablet:text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {DIALOG_CONTENT.description}
              </DialogPrimitive.Description>

              {/* Links */}
              <div className="flex flex-wrap gap-3 tablet:gap-4 mt-4">
                <a
                  href={DIALOG_CONTENT.links.privacyPolicy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs tablet:text-sm font-medium',
                    'text-[#014383] hover:text-[#003d7a] transition-colors'
                  )}
                >
                  {DIALOG_CONTENT.links.privacyPolicy.label}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <a
                  href={DIALOG_CONTENT.links.partnerList.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs tablet:text-sm font-medium',
                    'text-[#014383] hover:text-[#003d7a] transition-colors'
                  )}
                >
                  {DIALOG_CONTENT.links.partnerList.label}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {/* Categories */}
            <fieldset className="px-5 py-4 tablet:px-6 tablet:py-5 space-y-3 border-0 p-0 m-0">
              <legend className="sr-only">Ρυθμίσεις Cookies</legend>
              {COOKIE_CATEGORIES.map((category) => (
                <CookieCategory
                  key={category.id}
                  category={category}
                  checked={preferences[category.id]}
                  onChange={(value) => {
                    if (category.canToggle && category.id !== 'necessary') {
                      onUpdatePreference(category.id as keyof Omit<CookiePreferences, 'necessary'>, value);
                    }
                  }}
                />
              ))}
            </fieldset>
          </div>

          {/* Footer with buttons - fixed at bottom */}
          <div className="shrink-0 bg-white border-t border-gray-200 px-5 py-4 tablet:px-6 tablet:py-4 rounded-b-2xl tablet:rounded-b-3xl">
            {/* Mobile: Stack buttons vertically */}
            <div className="flex flex-col gap-2.5 tablet:hidden">
              <button
                type="button"
                onClick={onAcceptAll}
                className={cn(
                  'w-full px-4 py-3.5 rounded-xl font-bold text-sm',
                  // Header matching gradient
                  'bg-gradient-to-r from-[#014383] to-[#0159a8] text-white',
                  'hover:from-[#003d7a] hover:to-[#014383]',
                  'shadow-lg shadow-[#014383]/25 hover:shadow-xl',
                  'active:scale-[0.98] transition-all duration-200'
                )}
              >
                {DIALOG_CONTENT.buttons.acceptAll}
              </button>
              <button
                type="button"
                onClick={onAcceptSelected}
                className={cn(
                  'w-full px-4 py-3 rounded-xl font-semibold text-sm',
                  'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  'active:scale-[0.98] transition-all duration-200'
                )}
              >
                {DIALOG_CONTENT.buttons.acceptSelected}
              </button>
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'w-full px-4 py-2.5 rounded-xl font-medium text-sm',
                  'text-gray-600 hover:text-gray-800 hover:bg-gray-50',
                  'transition-colors duration-200 flex items-center justify-center gap-2'
                )}
              >
                <ArrowLeft className="h-4 w-4" />
                {DIALOG_CONTENT.buttons.back}
              </button>
            </div>

            {/* Tablet+: Horizontal layout */}
            <div className="hidden tablet:flex tablet:items-center tablet:justify-between">
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'px-4 py-2.5 rounded-xl font-medium text-sm',
                  'text-gray-600 hover:text-gray-800 hover:bg-gray-100',
                  'transition-colors duration-200 flex items-center gap-2'
                )}
              >
                <ArrowLeft className="h-4 w-4" />
                {DIALOG_CONTENT.buttons.back}
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onAcceptSelected}
                  className={cn(
                    'px-5 py-2.5 rounded-xl font-semibold text-sm',
                    'border-2 border-gray-300 text-gray-700',
                    'hover:border-gray-400 hover:bg-gray-50',
                    'active:scale-[0.98] transition-all duration-200'
                  )}
                >
                  {DIALOG_CONTENT.buttons.acceptSelected}
                </button>
                <button
                  type="button"
                  onClick={onAcceptAll}
                  className={cn(
                    'px-5 py-2.5 rounded-xl font-bold text-sm',
                    // Header matching gradient
                    'bg-gradient-to-r from-[#014383] to-[#0159a8] text-white',
                    'hover:from-[#003d7a] hover:to-[#014383]',
                    'shadow-lg shadow-[#014383]/25 hover:shadow-xl',
                    'active:scale-[0.98] transition-all duration-200'
                  )}
                >
                  {DIALOG_CONTENT.buttons.acceptAll}
                </button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
